"use client";

import { useEffect, ReactNode, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";

const FOCUSABLE_ELEMENT_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe:not([tabindex='-1'])",
  "details > summary:first-of-type",
  "[contenteditable]:not([contenteditable='false'])",
  "[tabindex]:not([tabindex='-1'])",
  "audio[controls]",
  "video[controls]",
].join(",");

interface BodyScrollLockState {
  count: number;
  previousOverflow: string;
  version: number;
}

interface FocusTrackerState {
  count: number;
  lastFocusedElement: HTMLElement | null;
  lastFocusedOutsideDialogs: HTMLElement | null;
  onFocusIn: (event: FocusEvent) => void;
}

interface FocusReturnTargets {
  preferred: HTMLElement | null;
  fallback: HTMLElement | null;
}

const bodyScrollLocks = new WeakMap<Document, BodyScrollLockState>();
const activeDialogs = new WeakMap<Document, HTMLElement[]>();
const focusTrackers = new WeakMap<Document, FocusTrackerState>();

function lockBodyScroll(ownerDocument: Document): () => void {
  let state = bodyScrollLocks.get(ownerDocument);

  if (!state) {
    state = {
      count: 0,
      previousOverflow: ownerDocument.body.style.overflow,
      version: 0,
    };
    bodyScrollLocks.set(ownerDocument, state);
  }

  state.count += 1;
  state.version += 1;
  ownerDocument.body.style.overflow = "hidden";

  let released = false;

  return () => {
    if (released) return;

    released = true;
    state.count = Math.max(0, state.count - 1);

    if (state.count !== 0) return;

    const releaseVersion = ++state.version;

    queueMicrotask(() => {
      if (state.count !== 0 || state.version !== releaseVersion) return;

      ownerDocument.body.style.overflow = state.previousOverflow;
      bodyScrollLocks.delete(ownerDocument);
    });
  };
}

function registerDialog(dialog: HTMLElement): void {
  const ownerDocument = dialog.ownerDocument;
  const dialogs = activeDialogs.get(ownerDocument) ?? [];
  const existingIndex = dialogs.indexOf(dialog);

  if (existingIndex !== -1) {
    dialogs.splice(existingIndex, 1);
  }

  dialogs.push(dialog);
  dialogs.sort((left, right) => {
    const position = left.compareDocumentPosition(right);
    const NodeConstructor = ownerDocument.defaultView?.Node;

    if (!NodeConstructor) return 0;
    if (position & NodeConstructor.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & NodeConstructor.DOCUMENT_POSITION_PRECEDING) return 1;

    return 0;
  });
  activeDialogs.set(ownerDocument, dialogs);
}

function unregisterDialog(dialog: HTMLElement): void {
  const ownerDocument = dialog.ownerDocument;
  const dialogs = activeDialogs.get(ownerDocument);

  if (!dialogs) return;

  const index = dialogs.lastIndexOf(dialog);

  if (index !== -1) {
    dialogs.splice(index, 1);
  }

  if (dialogs.length === 0) {
    activeDialogs.delete(ownerDocument);
  }
}

function topmostDialog(ownerDocument: Document): HTMLElement | undefined {
  return activeDialogs.get(ownerDocument)?.at(-1);
}

function isDialogRegistered(dialog: HTMLElement): boolean {
  return activeDialogs.get(dialog.ownerDocument)?.includes(dialog) ?? false;
}

function recordFocusedElement(
  ownerDocument: Document,
  state: FocusTrackerState,
  target: HTMLElement,
): void {
  const containingDialog = target.closest<HTMLElement>(
    "[data-modal-dialog='true']",
  );
  const currentDialog = topmostDialog(ownerDocument);

  if (containingDialog) {
    if (containingDialog !== currentDialog) return;

    state.lastFocusedElement = target;
    return;
  }

  if (currentDialog) return;

  state.lastFocusedElement = target;
  state.lastFocusedOutsideDialogs = target;
}

function acquireFocusTracker(ownerDocument: Document): () => void {
  let state = focusTrackers.get(ownerDocument);

  if (!state) {
    const newState: FocusTrackerState = {
      count: 0,
      lastFocusedElement: null,
      lastFocusedOutsideDialogs: null,
      onFocusIn: () => undefined,
    };

    newState.onFocusIn = (event) => {
      const view = ownerDocument.defaultView;
      const target = event.target;

      if (!view || !(target instanceof view.HTMLElement)) return;
      recordFocusedElement(ownerDocument, newState, target);
    };

    state = newState;
    focusTrackers.set(ownerDocument, state);
    ownerDocument.addEventListener("focusin", state.onFocusIn, true);
  }

  state.count += 1;

  const activeElement = activeHtmlElement(ownerDocument);
  if (activeElement) {
    recordFocusedElement(ownerDocument, state, activeElement);
  }

  let released = false;

  return () => {
    if (released) return;

    released = true;
    state.count = Math.max(0, state.count - 1);

    if (state.count === 0) {
      ownerDocument.removeEventListener("focusin", state.onFocusIn, true);
      focusTrackers.delete(ownerDocument);
    }
  };
}

function isElementVisible(element: HTMLElement, boundary: HTMLElement): boolean {
  if (element.closest("[hidden], [inert], [aria-hidden='true']")) return false;

  const view = element.ownerDocument.defaultView;
  let current: HTMLElement | null = element;

  while (view && current) {
    const style = view.getComputedStyle(current);

    if (style.display === "none" || style.visibility === "hidden") return false;
    if (current === boundary) break;

    current = current.parentElement;
  }

  return true;
}

function focusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR),
  ).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(":disabled") &&
      isElementVisible(element, dialog),
  );
}

function focusFirstElement(dialog: HTMLElement): void {
  const [first] = focusableElements(dialog);
  (first ?? dialog).focus({ preventScroll: true });
}

function activeHtmlElement(ownerDocument: Document): HTMLElement | null {
  const view = ownerDocument.defaultView;
  const activeElement = ownerDocument.activeElement;

  if (!view || !(activeElement instanceof view.HTMLElement)) return null;

  return activeElement;
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  contentClassName?: string;
  panelClassName?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
  contentClassName = "px-5 py-4",
  panelClassName = "",
}: ModalProps) {
  const t = useTranslations("common");
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  const returnFocusTargetsRef = useRef<FocusReturnTargets | null>(null);
  const pendingFocusRestoreRef = useRef<AbortController | null>(null);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const portalReady = portalTarget !== null;

  useEffect(() => acquireFocusTracker(document), []);

  useEffect(() => {
    function updatePortalTarget() {
      setPortalTarget(document.fullscreenElement ?? document.body);
    }

    updatePortalTarget();
    document.addEventListener("fullscreenchange", updatePortalTarget);

    return () => {
      document.removeEventListener("fullscreenchange", updatePortalTarget);
    };
  }, []);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    return lockBodyScroll(document);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const ownerDocument = dialog.ownerDocument;
    const activeElement = activeHtmlElement(ownerDocument);
    const focusTracker = focusTrackers.get(ownerDocument);

    const trackedFocus = focusTracker?.lastFocusedElement ?? null;
    pendingFocusRestoreRef.current?.abort();
    const focusRestore = new AbortController();
    pendingFocusRestoreRef.current = focusRestore;

    returnFocusTargetsRef.current = {
      preferred:
        activeElement &&
        activeElement !== ownerDocument.body &&
        !dialog.contains(activeElement)
          ? activeElement
          : trackedFocus && !dialog.contains(trackedFocus)
            ? trackedFocus
            : null,
      fallback: focusTracker?.lastFocusedOutsideDialogs ?? null,
    };

    return () => {
      const returnFocusTargets = returnFocusTargetsRef.current;
      returnFocusTargetsRef.current = null;

      queueMicrotask(() => {
        if (focusRestore.signal.aborted) return;

        const remainingDialog = topmostDialog(ownerDocument);
        let focusRestored = false;

        for (const returnFocus of [
          returnFocusTargets?.preferred,
          returnFocusTargets?.fallback,
        ]) {
          if (
            !returnFocus?.isConnected ||
            returnFocus.matches(":disabled") ||
            (remainingDialog && !remainingDialog.contains(returnFocus)) ||
            !isElementVisible(
              returnFocus,
              remainingDialog ?? ownerDocument.body,
            )
          ) {
            continue;
          }

          returnFocus.focus({ preventScroll: true });

          if (activeHtmlElement(ownerDocument) === returnFocus) {
            focusRestored = true;
            break;
          }
        }

        if (
          !focusRestored &&
          remainingDialog?.isConnected &&
          !remainingDialog.contains(activeHtmlElement(ownerDocument))
        ) {
          focusFirstElement(remainingDialog);
        }
      });
    };
  }, [open, portalReady]);

  useEffect(() => {
    if (!open || !portalTarget) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const ownerDocument = dialog.ownerDocument;

    registerDialog(dialog);

    if (
      topmostDialog(ownerDocument) === dialog &&
      !dialog.contains(activeHtmlElement(ownerDocument))
    ) {
      focusFirstElement(dialog);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        topmostDialog(ownerDocument) !== dialog
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = focusableElements(dialog);

      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = activeHtmlElement(ownerDocument);
      const currentIndex = current ? focusable.indexOf(current) : -1;

      if (currentIndex === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && currentIndex === 0) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && currentIndex === focusable.length - 1) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    function handleFocusIn(event: FocusEvent) {
      const view = ownerDocument.defaultView;
      const target = event.target;
      const containingDialog =
        view && target instanceof view.HTMLElement
          ? target.closest<HTMLElement>("[data-modal-dialog='true']")
          : null;

      if (
        topmostDialog(ownerDocument) !== dialog ||
        !dialog.isConnected ||
        (view && target instanceof view.Node && dialog.contains(target)) ||
        (containingDialog !== null && !isDialogRegistered(containingDialog))
      ) {
        return;
      }

      focusFirstElement(dialog);
    }

    ownerDocument.addEventListener("keydown", handleKeyDown);
    ownerDocument.addEventListener("focusin", handleFocusIn);

    return () => {
      ownerDocument.removeEventListener("keydown", handleKeyDown);
      ownerDocument.removeEventListener("focusin", handleFocusIn);
      unregisterDialog(dialog);
    };
  }, [open, portalTarget]);

  if (!open || !portalTarget) return null;

  const sizeClass = size === "sm" ? "modal-sm" : size === "lg" ? "modal-lg" : size === "xl" ? "modal-xl" : "modal-md";

  return createPortal((
    <div className="modal-overlay" data-shop-editor-ui="true" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className={`modal-box ${sizeClass} ${panelClassName}`}
        data-modal-dialog="true"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {title && (
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5 sm:py-4">
            <h3 id={titleId} className="min-w-0 truncate text-base font-semibold text-text">{title}</h3>
            <button
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface-hover hover:text-text"
              onClick={onClose}
              aria-label={t("close")}
            >
              ×
            </button>
          </div>
        )}
        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  ), portalTarget);
}

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: "danger" | "primary";
  loading?: boolean;
  closeOnConfirm?: boolean;
  error?: string | null;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmVariant = "danger",
  loading = false,
  closeOnConfirm = true,
  error,
}: ConfirmModalProps) {
  const t = useTranslations("common");

  function close() {
    if (!loading) onClose();
  }

  return (
    <Modal open={open} onClose={close} title={title ?? t("confirm")} size="sm">
      <p className="text-sm text-text-secondary leading-relaxed">{message}</p>
      {error ? <p role="alert" className="mt-3 text-sm text-danger">{error}</p> : null}
      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        <button type="button" className="btn-base btn-secondary" onClick={close} disabled={loading}>{t("cancel")}</button>
        <button
          type="button"
          className={confirmVariant === "danger" ? "btn-base btn-danger" : "btn-base btn-primary"}
          disabled={loading}
          aria-busy={loading}
          onClick={() => {
            if (loading) return;
            onConfirm();
            if (closeOnConfirm) onClose();
          }}
        >
          {confirmLabel ?? t("confirm")}
        </button>
      </div>
    </Modal>
  );
}
